import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getSaWebhookLogs,
  SaWebhookLog,
} from "../../../helpers/super_admin_ops_helper";

const PER_PAGE = 25;

/**
 * Últimos webhooks SES — diagnóstico ops (LAC-117).
 * Por quê: Roberto vê falhas de bounce/complaint sem abrir CloudWatch.
 */
const SuperAdminSesWebhooks = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  const [searchParams, setSearchParams] = useSearchParams();

  const initialOnlyErrors =
    searchParams.get("only_errors") === "1" ||
    searchParams.get("only_errors") === "true";
  const initialTenantRaw = searchParams.get("tenant_id") || "";
  const initialTenant =
    initialTenantRaw && /^\d+$/.test(initialTenantRaw)
      ? Number(initialTenantRaw)
      : undefined;

  const [items, setItems] = useState<SaWebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [onlyErrors, setOnlyErrors] = useState(initialOnlyErrors);
  const [tenantDraft, setTenantDraft] = useState(
    initialTenant != null ? String(initialTenant) : ""
  );
  const [tenantFilter, setTenantFilter] = useState<number | undefined>(
    initialTenant
  );

  document.title = t("sa.sesWebhooks.documentTitle");

  useEffect(() => {
    const next = new URLSearchParams();
    if (onlyErrors) next.set("only_errors", "1");
    if (tenantFilter != null) next.set("tenant_id", String(tenantFilter));
    setSearchParams(next, { replace: true });
  }, [onlyErrors, tenantFilter, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSaWebhookLogs({
        provider_slug: "ses",
        only_errors: onlyErrors ? 1 : undefined,
        tenant_id: tenantFilter,
        page,
        per_page: PER_PAGE,
      });
      if (!res?.success) {
        setError(t("sa.sesWebhooks.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : null;
      setError(msg || t("sa.sesWebhooks.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [onlyErrors, tenantFilter, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyTenantFilter = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = tenantDraft.trim();
    if (raw === "") {
      setTenantFilter(undefined);
      setPage(1);
      return;
    }
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) {
      setError(t("sa.sesWebhooks.filter.tenantInvalid"));
      return;
    }
    setTenantFilter(n);
    setPage(1);
  };

  const clearFilters = () => {
    setOnlyErrors(false);
    setTenantDraft("");
    setTenantFilter(undefined);
    setPage(1);
  };

  const formatWhen = (iso: string | null | undefined): string => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(locale);
    } catch {
      return iso;
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.sesWebhooks.title")}
          pageTitle={t("sa.menu.reputation")}
        />

        <Row className="mb-3">
          <Col lg={12}>
            <Alert color="info" className="mb-0 py-2">
              {t("sa.sesWebhooks.intro")}
            </Alert>
          </Col>
        </Row>

        <Card>
          <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <h5 className="card-title mb-0">{t("sa.sesWebhooks.tableTitle")}</h5>
            <form
              className="d-flex flex-wrap gap-2 align-items-center"
              onSubmit={applyTenantFilter}
            >
              <FormGroup check className="mb-0 me-2">
                <Input
                  id="ses-webhooks-only-errors"
                  type="checkbox"
                  checked={onlyErrors}
                  onChange={(e) => {
                    setOnlyErrors(e.target.checked);
                    setPage(1);
                  }}
                />
                <Label check for="ses-webhooks-only-errors" className="ms-1">
                  {t("sa.sesWebhooks.filter.onlyErrors")}
                </Label>
              </FormGroup>
              <Input
                type="text"
                bsSize="sm"
                style={{ maxWidth: 120 }}
                placeholder={t("sa.sesWebhooks.filter.tenantPlaceholder")}
                value={tenantDraft}
                onChange={(e) => setTenantDraft(e.target.value)}
                aria-label={t("sa.sesWebhooks.filter.tenantPlaceholder")}
              />
              <Button color="soft-primary" size="sm" type="submit">
                {t("sa.sesWebhooks.filter.apply")}
              </Button>
              {(onlyErrors || tenantFilter != null) && (
                <Button color="light" size="sm" type="button" onClick={clearFilters}>
                  {t("sa.sesWebhooks.filter.clear")}
                </Button>
              )}
              <Button
                color="soft-secondary"
                size="sm"
                type="button"
                disabled={loading}
                onClick={() => void load()}
              >
                {t("sa.sesWebhooks.refresh")}
              </Button>
            </form>
          </CardHeader>
          <CardBody>
            {error ? (
              <Alert color="danger" className="mb-3">
                {error}{" "}
                <button
                  type="button"
                  className="btn btn-sm btn-soft-danger ms-2"
                  onClick={() => void load()}
                >
                  {t("sa.sesWebhooks.retry")}
                </button>
              </Alert>
            ) : null}

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : items.length === 0 ? (
              <Alert color="secondary" className="mb-0">
                <p className="mb-2">{t("sa.sesWebhooks.empty")}</p>
                {(onlyErrors || tenantFilter != null) && (
                  <Button color="light" size="sm" onClick={clearFilters}>
                    {t("sa.sesWebhooks.filter.clear")}
                  </Button>
                )}
              </Alert>
            ) : (
              <>
                <div className="table-responsive">
                  <Table className="table-nowrap align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("sa.sesWebhooks.col.eventType")}</th>
                        <th>{t("sa.sesWebhooks.col.tenant")}</th>
                        <th>{t("sa.sesWebhooks.col.error")}</th>
                        <th>{t("sa.sesWebhooks.col.processedAt")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <span className="fw-medium">
                              {row.event_type || t("sa.sesWebhooks.unknownEvent")}
                            </span>
                            {row.http_status != null ? (
                              <Badge
                                color={
                                  row.http_status >= 400 ? "danger" : "secondary"
                                }
                                className="ms-2"
                              >
                                {row.http_status}
                              </Badge>
                            ) : null}
                            {String(row.event_type || "")
                              .toLowerCase()
                              .includes("bounce") &&
                            row.bounce_type === "Permanent" ? (
                              <Badge color="danger" className="ms-2">
                                {t("sa.sesWebhooks.bounce.permanent")}
                              </Badge>
                            ) : null}
                            {String(row.event_type || "")
                              .toLowerCase()
                              .includes("bounce") &&
                            row.bounce_type === "Transient" ? (
                              <Badge color="warning" className="ms-2">
                                {t("sa.sesWebhooks.bounce.soft")}
                              </Badge>
                            ) : null}
                          </td>
                          <td>
                            {row.tenant_id ? (
                              <Link
                                to={`/super-admin/tenants/${row.tenant_id}`}
                                className="fw-medium"
                              >
                                {row.tenant_name || `#${row.tenant_id}`}
                              </Link>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {row.error_message ? (
                              <span
                                className="text-danger text-break"
                                title={row.error_message}
                              >
                                {row.error_message.length > 120
                                  ? `${row.error_message.slice(0, 117)}…`
                                  : row.error_message}
                              </span>
                            ) : (
                              <Badge color="success">
                                {t("sa.sesWebhooks.ok")}
                              </Badge>
                            )}
                          </td>
                          <td className="text-muted small">
                            {formatWhen(row.processed_at || row.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">
                    {t("sa.sesWebhooks.pagination", {
                      total: total.toLocaleString(locale),
                      page,
                      last: lastPage,
                    })}
                  </span>
                  <div className="d-flex gap-2">
                    <Button
                      color="light"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      {t("sa.sesWebhooks.prev")}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      disabled={page >= lastPage}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t("sa.sesWebhooks.next")}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default SuperAdminSesWebhooks;
